import { Outlet, useNavigate, useRouterState } from "@tanstack/react-router";
import { type ReactNode, Suspense, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { Command } from "@/shared/lib/commands";
import { useGlobalShortcuts } from "@/shared/lib/hooks/useGlobalShortcuts";
import { useRegisterCommands } from "@/shared/lib/hooks/useCommands";
import { useShortcutsHelp } from "@/shared/lib/hooks/useShortcutsHelp";
import { useTheme } from "@/shared/lib/hooks/useTheme";
import { useMediaQuery } from "@/shared/lib/hooks/useMediaQuery";
import { requestNewChat } from "@/shared/lib/newChatSignal";
import { cn } from "@/shared/lib/cn";
import { CommandPalette, ShortcutsDialog, type ShortcutHint, TopbarSlotProvider } from "@/shared/ui";
import { NAV_ITEMS, type NavTo } from "../nav";
import { RoutePending } from "./RoutePending";
import { Sidebar } from "./Sidebar";
import { Topbar } from "./Topbar";

const SIDEBAR_STORAGE_KEY = "anyai-sidebar-open";

/** Leader-chord per primary destination (g then c/i/v — Linear-style). Combos
 *  like ⌘⇧I are reserved by the browser (DevTools), so navigation uses chords. */
const NAV_CHORDS: Record<NavTo, string> = {
  "/chat": "g c",
  "/images": "g i",
  "/videos": "g v",
  "/ugc": "g u",
  "/library": "g l",
  "/assistants": "g a",
  "/skills": "g s",
};

/** Read the persisted desktop sidebar state; defaults to expanded. */
function readSidebarOpen(): boolean {
  if (typeof localStorage === "undefined") return true;
  return localStorage.getItem(SIDEBAR_STORAGE_KEY) !== "false";
}

export interface AppShellProps {
  /** Conversation history rendered into the sidebar (composed in `app`). */
  sidebarHistory: ReactNode;
  /** Pinned-assistants section, between nav and history (composed in `app`). */
  sidebarPinned?: ReactNode;
  /** Account row at the foot of the sidebar (composed in `app`) — a render-prop
   *  handed the rail's collapsed state so it can shrink to just the cog. */
  sidebarFooter?: (collapsed: boolean) => ReactNode;
  /** Route-aware topbar actions (e.g. conversation export), composed in `app`. */
  topbarActions?: ReactNode;
}

/**
 * The frame every route mounts into: a fixed sidebar + topbar around a
 * scrolling content `Outlet`, matching the cendre-mono mockup in light and
 * dark. Owns the mobile drawer + scroll-shadow state, registers the global
 * navigation commands, and hosts the single ⌘K palette.
 */
export function AppShell({
  sidebarHistory,
  sidebarPinned,
  sidebarFooter,
  topbarActions,
}: AppShellProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { toggleTheme } = useTheme();
  const { setOpen: setShortcutsHelpOpen } = useShortcutsHelp();
  const [navOpen, setNavOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(readSidebarOpen);

  // Desktop-only collapse: below this width the sidebar is the overlay drawer
  // (driven by `navOpen`), so the collapse state must not apply there.
  const isDesktop = useMediaQuery("(min-width: 881px)");
  const railMode = isDesktop && !sidebarOpen;

  const setSidebarPersist = useCallback((open: boolean): void => {
    setSidebarOpen(open);
    try {
      localStorage.setItem(SIDEBAR_STORAGE_KEY, String(open));
    } catch {
      // ignore — a non-persisted toggle is fine.
    }
  }, []);

  // Toggle the right surface per breakpoint: the persisted desktop collapse, or
  // the mobile overlay drawer below the desktop threshold.
  const toggleSidebar = useCallback((): void => {
    if (isDesktop) setSidebarPersist(!sidebarOpen);
    else setNavOpen((open) => !open);
  }, [isDesktop, sidebarOpen, setSidebarPersist]);

  useGlobalShortcuts();

  // Close the mobile drawer on any navigation (covers slotted-in links, e.g. the
  // account footer, that don't own the drawer's close handler).
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  useEffect(() => setNavOpen(false), [pathname]);

  const commands = useMemo<Command[]>(() => {
    const navigation = t("commandPalette.navigation");
    const view = t("commandPalette.view");
    return [
      {
        id: "new-chat",
        title: t("commandPalette.newChat"),
        group: navigation,
        shortcut: "mod+shift+o",
        runInInput: true,
        perform: () => {
          requestNewChat();
          void navigate({ to: "/chat" });
        },
      },
      ...NAV_ITEMS.map((item) => ({
        id: `nav:${item.to}`,
        title: t(item.labelKey),
        group: navigation,
        chord: NAV_CHORDS[item.to],
        perform: () => void navigate({ to: item.to }),
      })),
      {
        id: "go-settings",
        title: t("commandPalette.settings"),
        group: navigation,
        shortcut: "mod+'",
        perform: () => void navigate({ to: "/settings", search: { section: "general" } }),
      },
      {
        id: "go-pricing",
        title: t("sidebar.pricing"),
        group: navigation,
        perform: () => void navigate({ to: "/pricing" }),
      },
      {
        id: "toggle-sidebar",
        title: t("commandPalette.toggleSidebar"),
        group: view,
        shortcut: "mod+b",
        perform: () => toggleSidebar(),
      },
      {
        id: "toggle-theme",
        title: t("settings.general.toggleTheme"),
        group: view,
        shortcut: "mod+shift+l",
        perform: () => toggleTheme(),
      },
      {
        id: "show-shortcuts",
        title: t("commandPalette.keyboardShortcuts"),
        group: view,
        shortcut: "mod+/",
        perform: () => setShortcutsHelpOpen(true),
      },
    ];
  }, [navigate, t, toggleTheme, setShortcutsHelpOpen, toggleSidebar]);

  useRegisterCommands(commands);

  // Single source of truth for the help sheet — the registered shortcuts above
  // plus the palette's own ⌘K (which the global handler owns directly).
  const shortcutHints = useMemo<ShortcutHint[]>(
    () => [
      { shortcut: "mod+k", label: t("commandPalette.searchPlaceholderShort") },
      { shortcut: "mod+shift+o", label: t("commandPalette.newChat") },
      { chord: NAV_CHORDS["/chat"], label: t("sidebar.chat") },
      { chord: NAV_CHORDS["/images"], label: t("sidebar.images") },
      { chord: NAV_CHORDS["/videos"], label: t("sidebar.videos") },
      { chord: NAV_CHORDS["/ugc"], label: t("sidebar.ugc") },
      { chord: NAV_CHORDS["/assistants"], label: t("sidebar.assistants") },
      { shortcut: "mod+b", label: t("commandPalette.toggleSidebar") },
      { shortcut: "mod+shift+l", label: t("settings.general.toggleTheme") },
      { shortcut: "mod+'", label: t("commandPalette.settings") },
      { shortcut: "mod+/", label: t("commandPalette.keyboardShortcuts") },
    ],
    [t],
  );

  return (
    <TopbarSlotProvider>
    <div
      className={cn(
        "grid h-screen overflow-hidden max-[880px]:grid-cols-1",
        "transition-[grid-template-columns] duration-300 ease-house [.canvas-resizing_&]:transition-none",
        railMode ? "min-[881px]:grid-cols-[69px_1fr]" : "min-[881px]:grid-cols-[280px_1fr]",
      )}
    >
      {navOpen ? (
        <button
          type="button"
          aria-label={t("sidebar.closeSidebar")}
          onClick={() => setNavOpen(false)}
          className="fixed inset-0 z-20 hidden cursor-default bg-ink/30 max-[880px]:block"
        />
      ) : null}

      <aside
        className={cn(
          "flex min-h-0 flex-col overflow-hidden bg-sidebar",
          "max-[880px]:fixed max-[880px]:inset-y-0 max-[880px]:left-0 max-[880px]:z-30 max-[880px]:w-[284px] max-[880px]:shadow-[var(--shadow-pill)] max-[880px]:transition-transform max-[880px]:duration-300 max-[880px]:ease-house",
          navOpen ? "max-[880px]:translate-x-0" : "max-[880px]:-translate-x-[104%]",
        )}
      >
        <Sidebar
          history={sidebarHistory}
          pinned={sidebarPinned}
          footer={sidebarFooter}
          onNavigate={() => setNavOpen(false)}
          onCollapse={() => setSidebarPersist(false)}
          onExpand={() => setSidebarPersist(true)}
          collapsed={railMode}
        />
      </aside>

      <main className="relative flex min-h-0 min-w-0 flex-col bg-canvas transition-[padding] duration-300 ease-house min-[881px]:[padding-right:var(--right-rail,0px)] [.canvas-resizing_&]:transition-none">
        <Topbar scrolled={scrolled} onOpenMenu={() => setNavOpen(true)} actions={topbarActions} />
        <div
          onScroll={(event) => setScrolled(event.currentTarget.scrollTop > 8)}
          className="min-h-0 flex-1 overflow-y-auto scroll-smooth [scrollbar-gutter:stable_both-edges]"
        >
          {/* Suspense boundary for the lazily-loaded feature routes (images,
              videos, settings, pricing). Shows the standard route-pending UI
              while the route's chunk loads. */}
          <Suspense fallback={<RoutePending />}>
            <Outlet />
          </Suspense>
        </div>
      </main>

      <CommandPalette />
      <ShortcutsDialog hints={shortcutHints} />
    </div>
    </TopbarSlotProvider>
  );
}
